import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import apiUtils from '../../global/utils/api';

import {
  handleCreateFulfilled
  , handleFetchSinglePending
  , handleFetchSingleFulfilled
  , handleFetchSingleFromListFulfilled
  , handleFetchSingleRejected
  , handleFetchListPending
  , handleFetchListFulfilled
  , handleFetchListRejected
  , handleMutationPending
  , handleMutationFulfilled
  , handleMutationRejected
  , handleDeletePending
  , handleDeleteFulfilled
  , handleDeleteRejected
  , shouldFetch
  , INITIAL_STATE
  , handleInvalidateQuery
  , handleInvalidateQueries
  , handleAddSingleToList
} from '../../global/utils/storeUtils';

// related actions from other slices
import { fetchGroupListAtEndpoint, sendUpdateGroup } from '../group/groupStore';


// First define all API calls for organization
// define and export the strings for the different specific product endpoints once here.
// we'll catch for these strings on the server side and apply the correct permissions to the query.
// these are passed in to the productService hooks at the component level as the endpoint argument.
export const myOrganizationsEndpoint = 'logged-in';
export const publicOrganizationsEndpoint = 'public';
/**
 * The functions below, called thunks, allow us to perform async logic. They
 * can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
 * will call the thunk with the `dispatch` function as the first argument. Async
 * code can then be executed and other actions can be dispatched. Thunks are
 * typically used to make async requests.
 * 
 * In practice we won't dispatch these directly, they will be dispatched by organizationService which has a nicer api built on hooks.
 */

// CREATE
export const sendCreateOrganization = createAsyncThunk(
  'organization/sendCreate'
  , async (newOrganization) => {
    const endpoint = `/api/organizations`;
    const response = await apiUtils.callAPI(endpoint, 'POST', newOrganization);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// READ
export const fetchSingleOrganization = createAsyncThunk(
  'organization/fetchSingle'
  , async (id) => {
    const endpoint = `/api/organizations/${id}`;
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);
export const fetchOrganizationList = createAsyncThunk(
  'organization/fetchList' // this is the action name that will show up in the console logger.
  , async (listArgs) => {
    const endpoint = `/api/organizations${listArgs}`;
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// for each resource we can add as many endpoints as we want in this format and we only need two actions to handle them.
// this will hit the same endpoint as the list version, but the store will handle the returned array and access the single item in it.
export const fetchSingleOrganizationAtEndpoint = createAsyncThunk(
  'organization/fetchSingleWithFilter' // this is the action name that will show up in the console logger.
  , async (query) => {
    const endpoint = `/api/organizations${query}`
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);
export const fetchOrganizationListAtEndpoint = createAsyncThunk(
  'organization/fetchListWithFilter' // this is the action name that will show up in the console logger.
  , async (query) => {
    const endpoint = `/api/organizations${query}`; // example: `/api/organizations/logged-in?${queryString}`
    const response = await apiUtils.callAPI(endpoint);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// UPDATE
export const sendUpdateOrganization = createAsyncThunk(
  'organization/sendUpdate'
  , async ({ _id, ...updates }) => {
    const endpoint = `/api/organizations/${_id}`;
    const response = await apiUtils.callAPI(endpoint, 'PUT', updates);
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// DELETE
export const sendDeleteOrganization = createAsyncThunk(
  'organization/sendDelete'
  , async (id) => {
    const endpoint = `/api/organizations/${id}`;
    const response = await apiUtils.callAPI(endpoint, 'DELETE');
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

// next define the store's initial state, all of our store utils rely on a specific state shape, so use the constant
const initialState = { ...INITIAL_STATE };

// define the organizationSlice. This is a combination of actions and reducers. More info: https://redux-toolkit.js.org/api/createSlice
export const organizationSlice = createSlice({
  name: 'organization'
  , initialState
  /**
   * The `reducers` field lets us define reducers and generate associated actions.
   * Unlike the selectors defined at the bottom of this file, reducers only have access
   * to this specific reducer and not the entire store.
   * 
   * Again, we will not dispatch these directly, they will be dispatched by organizationService.
   */
  , reducers: {
    invalidateQuery: handleInvalidateQuery
    , invalidateQueries: handleInvalidateQueries
    , addOrganizationToList: handleAddSingleToList
  }

  /**
   * The `extraReducers` field lets the slice handle actions defined elsewhere,
   * including actions generated by createAsyncThunk or in other slices.
   * We'll use them to track our server request status.
   * 
   * We'll add a case for each API call defined at the top of the file to dictate
   * what happens during each API call lifecycle.
   */
  , extraReducers: (builder) => {
    builder
      // CREATE
      .addCase(sendCreateOrganization.fulfilled, handleCreateFulfilled)

      // READ
      .addCase(fetchSingleOrganization.pending, handleFetchSinglePending)
      .addCase(fetchSingleOrganization.fulfilled, handleFetchSingleFulfilled)
      .addCase(fetchSingleOrganization.rejected, handleFetchSingleRejected)
      .addCase(fetchOrganizationList.pending, handleFetchListPending)
      // because lists are returned from the server named for their resource, we need to pass a `listKey` so the util can properly handle the response
      .addCase(fetchOrganizationList.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'organizations'))
      .addCase(fetchOrganizationList.rejected, handleFetchListRejected)

      // permission protected single fetches
      .addCase(fetchSingleOrganizationAtEndpoint.pending, handleFetchSinglePending)
      // these endpoints return named lists, we need to pass a `listKey` so the util can properly handle the response
      .addCase(fetchSingleOrganizationAtEndpoint.fulfilled, (state, action) => handleFetchSingleFromListFulfilled(state, action, 'organizations'))
      .addCase(fetchSingleOrganizationAtEndpoint.rejected, handleFetchSingleRejected)
      // permission protected list fetches
      .addCase(fetchOrganizationListAtEndpoint.pending, handleFetchListPending)
      .addCase(fetchOrganizationListAtEndpoint.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'organizations'))
      .addCase(fetchOrganizationListAtEndpoint.rejected, handleFetchListRejected)

      // catch for groupFetches that return their organizations
      .addCase(fetchGroupListAtEndpoint.pending, handleFetchListPending)
      .addCase(fetchGroupListAtEndpoint.fulfilled, (state, action) => handleFetchListFulfilled(state, action, 'organizations'))
      .addCase(fetchGroupListAtEndpoint.rejected, handleFetchListRejected)

      // UPDATE
      .addCase(sendUpdateOrganization.pending, handleMutationPending)
      .addCase(sendUpdateOrganization.fulfilled, handleMutationFulfilled)
      .addCase(sendUpdateOrganization.rejected, handleMutationRejected)
      .addCase(sendUpdateGroup.fulfilled, (state, action) => {
        // check for organization and update it
        if (action?.payload?.organization) {
          const newPayload = action.payload.organization;
          // can't mutate action.payload directly, so we need to create a new action
          handleMutationFulfilled(state, { ...action, payload: newPayload });
        }
      })
      // .addCase(sendUpdateOrganization.fulfilled, (state, action) => handleMutationFulfilled(state, action, (newState, action) => {
      //   // by passing this optional callback we now have access to the new state if we want to do something else with it, this works for all reducer handlers
      // }))

      // DELETE
      .addCase(sendDeleteOrganization.pending, handleDeletePending)
      .addCase(sendDeleteOrganization.fulfilled, handleDeleteFulfilled)
      .addCase(sendDeleteOrganization.rejected, handleDeleteRejected)
  }
});

// export the actions for the reducers defined above
export const { invalidateQuery, invalidateQueries, addOrganizationToList } = organizationSlice.actions;


// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.
export const fetchListIfNeeded = (queryKey, listFetch = fetchOrganizationList) => (dispatch, getState) => {
  const organizationQuery = getState().organization.listQueries[queryKey];
  if(shouldFetch(organizationQuery)) {
    // console.log('Fetching organization list', queryKey);
    dispatch(listFetch(queryKey));
  } else {
    // console.log('No need to fetch, fresh query in cache');
  }
};

export const fetchSingleIfNeeded = (id, singleFetch = fetchSingleOrganization) => (dispatch, getState) => {
  const organizationQuery = getState().organization.singleQueries[id];
  if(shouldFetch(organizationQuery)) {
    dispatch(singleFetch(id));
  } else {
    // console.log('No need to fetch, fresh query in cache');
  }
}

export default organizationSlice.reducer;
